import { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import type { PMIssueComment, UserProfile } from "@/domains/pm/types";
import { useProjectMembersStore } from "@/stores/pm/hooks/usePMStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { formatCommentTimestamp, panelDateLocale } from "@/domains/pm/utils/panelDates";
import { composeReplyBody, quoteTextOf } from "@/domains/pm/utils/commentQuote";
import { getDisplayName } from "@/domains/pm/utils/commentAuthor";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import { ChatThreadRow, type ChatRowLabels } from "@/components/chat/thread/ChatThreadRow";
import { ChatComposer, type ChatComposerHandle } from "@/components/chat/thread/ChatComposer";
import { ISSUE_ATTACHMENTS_FOLDER_NAME } from "@/domains/documents/services/folderService";
import { PanelSectionLabel } from "./PanelSectionLabel";

interface IssueCommentsProps {
  comments: PMIssueComment[];
  issueId: number;
  /** Building the issue belongs to — required for the attachment picker/uploader (WHA-3376). */
  buildingId: number;
  /** Project of the issue — required for the upload helper. */
  buildingProjectId: number;
  /** Shared previewer so attachment chips open the same dialog as the rest of the panel. */
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  /** Resolve `false` when nothing was posted: the composer restores the draft and its quote. */
  onAddComment: (
    body: string,
    mentionedUserIds: number[],
    documentIds: number[]
  ) => void | boolean | Promise<boolean>;
  /** WHA-3453: edit one of the user's own comments. */
  onEditComment: (commentId: number, body: string, mentionedUserIds: number[]) => void;
  /** WHA-3453: the viewing user — only their own comments get an Edit affordance. */
  currentUserId?: number;
  /** WHA-3810: the viewing user's profile — its avatar leads the input row. */
  currentUser?: UserProfile | null;
  /** Read the thread without being able to add to it — snapshot mode. The
   *  comments still show; only the composer goes. */
  readOnly?: boolean;
  /** The thread has not been read yet — the panel opens before it lands. */
  loading?: boolean;
}

export const IssueComments = observer(function IssueComments({
  comments,
  buildingId,
  buildingProjectId,
  openViewer,
  onAddComment,
  onEditComment,
  currentUserId,
  currentUser,
  readOnly = false,
  loading = false,
}: IssueCommentsProps) {
  const { t, language } = useLanguage();
  // Mention highlight/resolution must use the SAME audience as the picker: the
  // project-scoped member list (org owners/admins + assigned subs), not the
  // org-wide user list. Otherwise names outside the loaded org set fail to
  // highlight and get silently dropped on edit.
  const projectMembers = useProjectMembersStore();
  useEffect(() => {
    if (buildingProjectId) projectMembers.fetch(buildingProjectId);
  }, [buildingProjectId, projectMembers]);
  const projectUsers = projectMembers.get(buildingProjectId);
  const mentionDisplayNames = useMemo(
    () =>
      projectUsers
        .map((u) => {
          const full = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
          return full || u.email;
        })
        .filter((n): n is string => Boolean(n)),
    [projectUsers]
  );
  // WHA-3810 round 3: the reply quote is composer state, NOT text in the
  // textarea — a user must not be able to rewrite someone else's words inside
  // the quote before sending. It renders as a read-only block above the input
  // and is only re-attached to the body on submit.
  const [quote, setQuote] = useState<string | null>(null);
  const composerRef = useRef<ChatComposerHandle>(null);
  // WHA-3453: which comment is being edited, and its in-progress text.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState("");

  // WHA-3453: derive the mentioned user ids from a comment body by matching
  // `@<DisplayName>` against the project member list — same basis as the
  // highlight, so an edit keeps/drops mentions to match the final text.
  const deriveMentionIds = (text: string): number[] => {
    const ids: number[] = [];
    for (const u of projectUsers) {
      const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.email;
      if (name && text.includes(`@${name}`)) ids.push(u.id);
    }
    return ids;
  };

  const startEdit = (comment: PMIssueComment) => {
    setEditingId(comment.id);
    setEditBody(comment.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody("");
  };

  const saveEdit = (comment: PMIssueComment) => {
    const trimmed = editBody.trim();
    if (!trimmed) return;
    if (trimmed !== comment.body) {
      onEditComment(comment.id, trimmed, deriveMentionIds(trimmed));
    }
    setEditingId(null);
    setEditBody("");
  };

  // WHA-3810: reply to a comment — quote it into the composer, mention its
  // author (so they get the notification), and hand over the caret. The quote
  // is held apart from the editable body and only merged in on send, which is
  // why no comment thread schema is involved.
  const handleReply = (comment: PMIssueComment) => {
    const author = projectUsers.find((u) => u.id === comment.author_id);
    // Use the project-member spelling of the name so the mention resolves on
    // send; fall back to the comment's own author record for ex-members.
    const name = author
      ? `${author.first_name ?? ""} ${author.last_name ?? ""}`.trim() || author.email
      : getDisplayName(comment);
    // Attachment-only parent (empty body) quotes as "" — hold null instead so
    // no empty quote block renders, matching composeReplyBody sending none.
    setQuote(quoteTextOf(comment.body) || null);
    composerRef.current?.appendMention({ userId: author?.id, displayName: name });
  };

  const handleSend = (
    body: string,
    mentionedUserIds: number[],
    documentIds: number[]
  ): Promise<boolean> => {
    const sentQuote = quote;
    setQuote(null);
    // The quote goes on the wire as the leading `> …` run the sent-comment
    // renderer already understands — unchanged by anything the user typed.
    const accepted = onAddComment(composeReplyBody(sentQuote, body), mentionedUserIds, documentIds);
    return Promise.resolve(accepted).then((result) => {
      if (result !== false) return true;
      // The composer restores the draft; the quote it was replying under goes back with it.
      setQuote((current) => current ?? sentQuote);
      return false;
    });
  };

  const timestampLabels = {
    justNow: t.pm.issues.detail.justNow,
    yesterday: t.pm.issues.dateBuckets.yesterday,
    locale: panelDateLocale(language),
  };

  const rowLabels: ChatRowLabels = {
    reply: t.pm.issues.detail.reply,
    edit: t.pm.issues.detail.edit,
    edited: t.pm.issues.detail.edited,
    save: t.pm.common.save,
    cancel: t.pm.common.cancel,
  };

  return (
    // WHA-3810: the comment area flows with the rest of the panel body — its
    // own section label, then the thread, then the input directly under it.
    // The panel owns the single scroll container, so any leftover space falls
    // BELOW the input rather than opening a gap between the content and it.
    <div className="py-2.5">
      {/* 12px under the label — the avatar-led comment row reads tighter than
          the other sections' content at 6px (WHA-3810 round 3). */}
      <PanelSectionLabel className="mb-3">
        {loading && comments.length === 0
          ? t.pm.issues.detail.comments
          : `${t.pm.issues.detail.comments} · ${comments.length}`}
      </PanelSectionLabel>
      {loading && comments.length === 0 && (
        <p className="text-xs text-muted-foreground" aria-live="polite">
          {t.pm.issues.detail.loadingComments}
        </p>
      )}
      <div className="space-y-3">
        {comments.map((comment) => (
          <ChatThreadRow
            key={comment.id}
            author={comment.author}
            authorName={getDisplayName(comment)}
            timestamp={formatCommentTimestamp(comment.created_at, timestampLabels)}
            edited={Boolean(comment.modified_at)}
            body={comment.body}
            mentionDisplayNames={mentionDisplayNames}
            attachments={comment.linked_documents}
            buildingId={buildingId}
            projectId={buildingProjectId}
            openViewer={openViewer}
            labels={rowLabels}
            canEdit={currentUserId != null && comment.author_id === currentUserId}
            isEditing={editingId === comment.id}
            editBody={editBody}
            onEditBodyChange={setEditBody}
            onStartEdit={() => startEdit(comment)}
            onCancelEdit={cancelEdit}
            onSaveEdit={() => saveEdit(comment)}
            onReply={() => handleReply(comment)}
          />
        ))}
      </div>

      {/* Input row — WHA-3810: sits directly under the thread, wrapped in a
          rounded pill border so avatar, composer and actions read as one
          defined control. */}
      {!readOnly && (
        <ChatComposer
          handleRef={composerRef}
          buildingId={buildingId}
          projectId={buildingProjectId}
          currentUser={currentUser}
          quote={quote}
          onRemoveQuote={() => setQuote(null)}
          onSend={handleSend}
          targetFolderName={ISSUE_ATTACHMENTS_FOLDER_NAME}
          className="pt-2"
          labels={{
            placeholder: t.pm.issues.detail.addComment,
            attach: t.pm.issues.detail.attach,
            mention: t.pm.issues.detail.mention,
            send: t.pm.issues.detail.send,
            removeQuote: t.pm.issues.detail.removeQuote,
          }}
        />
      )}
    </div>
  );
});
